/**
 * The one KPI tile for every page (spec §4.3), replacing `company/MetricCard.tsx`
 * + `company/HeroKpi.tsx`, `BranchDashboard.tsx`'s local `HeroKpi`, the queues
 * `Kpi`, and the ad-hoc Fluent `Card` tiles in `MyDashboard.tsx` /
 * `dashboard/*Card.tsx`.
 *
 * Shape (spec §4.3): soft elevated opaque card -- `colorNeutralBackground1`,
 * `borderRadiusLarge`, `shadow4`, padding `L`, left-aligned. The label is set
 * in the teal highlight color (`useChartColors().outbound` -- the same token
 * `PageHeader`'s stripe/breadcrumb use, per spec §2.1) and is the tile's only
 * accent: no top bar, no underline, no edge bar.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { makeStyles, mergeClasses, tokens, Caption1, Caption1Strong, Title1, LargeTitle, Tooltip } from '@fluentui/react-components';
import { LoadingState } from './LoadingState';
import { ArrowUp16Regular, ArrowDown16Regular, ArrowRight16Regular, Info16Regular } from '@fluentui/react-icons';
import type { EChartsOption } from 'echarts';
import { Chart } from './charts/Chart';
import { useChartColors } from './charts/chartTheme';

export type KpiTileSize = 'hero' | 'tile';
export type KpiTileTone = 'neutral' | 'positive' | 'negative' | 'warning';
export type KpiTileDeltaDirection = 'up' | 'down' | 'flat';
export type KpiTileSubItemStatus = 'good' | 'warning' | 'poor';

export interface KpiTileDelta {
  text: string;
  direction: KpiTileDeltaDirection;
}

/**
 * One entry in the optional `subItems` list (spec §4.3, amended
 * 2026-09-13). A short, fully-typed sub-stat/breakdown row -- not an
 * open-content slot -- so `KpiTile` covers cards like `CallVolumeCard`'s
 * inbound/outbound sub-stats or `BadCallPercentageCard`'s jitter/latency/
 * packet-loss breakdown without becoming a generic wrapper.
 */
export interface KpiTileSubItem {
  icon?: ReactNode;
  label: string;
  value: string | number;
  /** Reuses `QualityIndicator`'s good/warning/poor palette -- no new color. */
  status?: KpiTileSubItemStatus;
}

export interface KpiTileProps {
  label: string;
  /**
   * The tile's headline number. A raw `number` animates as a plain
   * count-up. A pre-formatted `string` animates too when it matches a
   * recognized shape (comma-grouped integer/decimal, a trailing `%`, or an
   * `m:ss` / `h:mm:ss` duration -- spec §4.3: "`m:ss` values count the
   * seconds") -- format is preserved across every animated frame. Any other
   * string (e.g. "Unavailable") renders immediately, unanimated.
   */
  value: string | number;
  caption?: string;
  size: KpiTileSize;
  delta?: KpiTileDelta;
  sparkline?: number[];
  /** Decides the delta chip's color. `delta.direction` decides only the arrow glyph -- the two are independent (spec §4.3). */
  tone?: KpiTileTone;
  /** Colors the headline value text itself, independent of `tone` (spec §4.3, amended 2026-09-13). */
  valueTone?: KpiTileTone;
  /** Short sub-stat/breakdown list rendered under the value row (spec §4.3, amended 2026-09-13). */
  subItems?: KpiTileSubItem[];
  /**
   * Keep the tile's frame and label and show a content-shaped skeleton in
   * place of the value, caption and sub-items, so a loading dashboard keeps
   * the card grid it will have once the data lands (2026-09-20).
   */
  loading?: boolean;
  /** Info-icon tooltip beside the label (spec §4.3, amended 2026-09-13). */
  hint?: string;
  className?: string;
  /**
   * Keeps the headline value on one physical line (owner send-back F5a/F2):
   * a formatted date-time value ("Sep 15, 1:54 PM") can otherwise wrap
   * mid-value inside a narrow tile, splitting the trailing AM/PM onto its
   * own line. Off by default -- most tile values are short numbers/durations
   * that never need it.
   */
  noWrapValue?: boolean;
}

const useStyles = makeStyles({
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    textAlign: 'left',
    minWidth: 0,
  },
  hero: {
    gap: tokens.spacingVerticalM,
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  tileValue: {
    fontVariantNumeric: 'tabular-nums',
  },
  heroValue: {
    fontVariantNumeric: 'tabular-nums',
  },
  noWrapValue: {
    whiteSpace: 'nowrap',
  },
  caption: {
    color: tokens.colorNeutralForeground3,
  },
  delta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: tokens.borderRadiusCircular,
    fontSize: '12px',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  deltaPositive: {
    color: tokens.colorPaletteGreenForeground1,
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  deltaNegative: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground2,
  },
  deltaNeutral: {
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  deltaWarning: {
    color: tokens.colorPaletteYellowForeground1,
    backgroundColor: tokens.colorPaletteYellowBackground2,
  },
  sparkline: {
    minHeight: '80px',
    marginTop: tokens.spacingVerticalXS,
  },
  labelRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  hintIcon: {
    color: tokens.colorNeutralForeground3,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'help',
  },
  subItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  subItemsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: tokens.spacingHorizontalM,
    rowGap: tokens.spacingVerticalXS,
  },
  subItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  subItemIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '16px',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  subItemText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  subItemValue: {
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  subItemLabel: {
    color: tokens.colorNeutralForeground3,
  },
  subItemGood: {
    color: tokens.colorPaletteGreenForeground1,
  },
  subItemWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },
  subItemPoor: {
    color: tokens.colorPaletteRedForeground1,
  },
});

const DELTA_ICON: Record<KpiTileDeltaDirection, JSX.Element> = {
  up: <ArrowUp16Regular />,
  down: <ArrowDown16Regular />,
  flat: <ArrowRight16Regular />,
};

const DELTA_TONE_CLASS_KEY: Record<KpiTileTone, 'deltaPositive' | 'deltaNegative' | 'deltaNeutral' | 'deltaWarning'> = {
  positive: 'deltaPositive',
  negative: 'deltaNegative',
  neutral: 'deltaNeutral',
  warning: 'deltaWarning',
};

/** `valueTone` colors the headline text directly, unlike the delta chip's background+foreground pairing. */
const VALUE_TONE_COLOR: Record<KpiTileTone, string | undefined> = {
  positive: tokens.colorPaletteGreenForeground1,
  negative: tokens.colorPaletteRedForeground1,
  warning: tokens.colorPaletteYellowForeground1,
  neutral: undefined,
};

const SUB_ITEM_STATUS_CLASS_KEY: Record<KpiTileSubItemStatus, 'subItemGood' | 'subItemWarning' | 'subItemPoor'> = {
  good: 'subItemGood',
  warning: 'subItemWarning',
  poor: 'subItemPoor',
};

/** `1,234`, `1234.5` -- not a duration (no `:`), not a percent (no trailing `%`). */
const NUMERIC_PATTERN = /^-?[\d,]+(\.\d+)?$/;
const PERCENT_PATTERN = /^(-?[\d,]+(\.\d+)?)%$/;
/** `m:ss` or `h:mm:ss`, per spec §4.3. */
const DURATION_PATTERN = /^(\d+):(\d{2})(?::(\d{2}))?$/;

interface Animatable {
  final: number;
  format: (n: number) => string;
}

function parseAnimatable(value: string | number): Animatable | null {
  if (typeof value === 'number') {
    return { final: value, format: (n) => Math.round(n).toLocaleString() };
  }
  const trimmed = value.trim();

  const durationMatch = DURATION_PATTERN.exec(trimmed);
  if (durationMatch) {
    const hasHours = durationMatch[3] !== undefined;
    const totalSeconds = hasHours
      ? Number(durationMatch[1]) * 3600 + Number(durationMatch[2]) * 60 + Number(durationMatch[3])
      : Number(durationMatch[1]) * 60 + Number(durationMatch[2]);
    return {
      final: totalSeconds,
      format: (n) => {
        const s = Math.max(0, Math.round(n));
        const hh = Math.floor(s / 3600);
        const mm = Math.floor((s % 3600) / 60);
        const ss = s % 60;
        return hasHours || hh > 0
          ? `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
          : `${mm}:${String(ss).padStart(2, '0')}`;
      },
    };
  }

  const percentMatch = PERCENT_PATTERN.exec(trimmed);
  if (percentMatch) {
    const raw = percentMatch[1];
    const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
    const final = Number(raw.replace(/,/g, ''));
    if (Number.isNaN(final)) return null;
    return { final, format: (n) => `${n.toFixed(decimals)}%` };
  }

  if (NUMERIC_PATTERN.test(trimmed)) {
    const decimals = trimmed.includes('.') ? trimmed.split('.')[1].length : 0;
    const final = Number(trimmed.replace(/,/g, ''));
    if (Number.isNaN(final)) return null;
    return {
      final,
      format: (n) => n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }),
    };
  }

  return null;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/**
 * Fires `onEnter` once, the first time `el` crosses 60% viewport
 * visibility. Shared by the count-up and sparkline-draw entrances (spec
 * §4.3: both trigger "the first time it enters the viewport ... not on page
 * mount").
 */
function useOnceVisible(el: Element | null, onEnter: (becameVisibleAt: number) => void, deps: unknown[]) {
  const firedRef = useRef(false);
  useEffect(() => {
    if (!el || firedRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || entry.intersectionRatio < 0.6 || firedRef.current) return;
        firedRef.current = true;
        observer.disconnect();
        onEnter(Date.now());
      },
      { threshold: [0, 0.6, 1] },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Drives the "count from 0" entrance (spec §4.3): plays once, only the first
 * time the tile crosses 60% viewport visibility, and only if the value was
 * already animatable within 2s of that moment (the "slow-path exception" --
 * otherwise, or under `prefers-reduced-motion`, the final value renders with
 * no count-up). Once the one-time window has resolved (animated or
 * skipped), the raw `value` prop is rendered directly, so a later data
 * refresh always shows immediately and never re-animates.
 */
function useCountUp(value: string | number): { ref: (el: HTMLSpanElement | null) => void; display: string } {
  const [el, setEl] = useState<HTMLSpanElement | null>(null);
  const [display, setDisplay] = useState<string | number>(value);
  const settledRef = useRef(false);
  const mountedAtRef = useRef(Date.now());
  // "Latest ref" pattern, updated unconditionally every render (not inside
  // an effect): `useOnceVisible`'s observer is created once, when `el`
  // first mounts, and its callback fires later, asynchronously, on the
  // browser's own schedule. If it closed over `value` directly it would
  // freeze whatever `value` was on that first render -- for a tile whose
  // data (e.g. `HeroKpi`'s `total`, computed from a separately-loading
  // `rows` hook) arrives a beat after mount, that is 0, and since `value`
  // never changes again once the real number lands on a re-render that
  // predates the observer firing, the settle-effect below has nothing left
  // to correct: the tile gets stuck showing 0 forever (found live on
  // /company against real data 2026-09-13). Reading `latestValueRef.current`
  // instead always reflects whatever `value` actually is at the moment the
  // observer fires, however late that is.
  const latestValueRef = useRef(value);
  latestValueRef.current = value;

  useOnceVisible(
    el,
    (becameVisibleAt) => {
      const animatable = parseAnimatable(latestValueRef.current);
      settledRef.current = true;
      if (!animatable || prefersReducedMotion() || becameVisibleAt - mountedAtRef.current > 2000) {
        setDisplay(animatable ? animatable.format(animatable.final) : latestValueRef.current);
        return;
      }
      const duration = 400;
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        setDisplay(animatable.format(animatable.final * easeOutCubic(t)));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    },
    [el],
  );

  useEffect(() => {
    if (settledRef.current) setDisplay(value);
  }, [value]);

  return { ref: setEl, display: typeof display === 'number' ? display.toLocaleString() : display };
}

/**
 * Gates the sparkline's 500ms left-to-right draw (spec §4.3) behind the same
 * once-visible rule as `useCountUp`. `Chart` itself isn't mounted until this
 * fires, so ECharts plays its own line-draw entrance animation (the same
 * `animationDuration`/`animationEasing` idiom `company/HeroKpi.tsx` and
 * `BranchDashboard.tsx`'s local `HeroKpi` already use) exactly once, the
 * first time the tile enters the viewport, instead of on mount.
 */
function useSparklineVisible(hasSparkline: boolean): { ref: (el: HTMLDivElement | null) => void; visible: boolean } {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useOnceVisible(hasSparkline ? el : null, () => setVisible(true), [el, hasSparkline]);

  return { ref: setEl, visible };
}

export function KpiTile({
  label,
  value,
  caption,
  size,
  delta,
  sparkline,
  tone = 'neutral',
  valueTone = 'neutral',
  subItems,
  hint,
  loading = false,
  className,
  noWrapValue = false,
}: KpiTileProps) {
  const styles = useStyles();
  const colors = useChartColors();
  const { ref: valueRef, display: displayValue } = useCountUp(value);
  const { ref: sparkRef, visible: sparkVisible } = useSparklineVisible(Boolean(sparkline?.length));
  const reducedMotion = useMemo(() => prefersReducedMotion(), []);
  const valueColor = VALUE_TONE_COLOR[valueTone];

  const sparkOption: EChartsOption | null = useMemo(() => {
    if (!sparkline?.length) return null;
    return {
      grid: { left: 0, right: 0, top: 4, bottom: 0, containLabel: false },
      xAxis: { type: 'category', show: false, data: sparkline.map((_, i) => i) },
      yAxis: { type: 'value', show: false, min: Math.min(0, ...sparkline), max: Math.max(...sparkline) },
      series: [
        {
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: sparkline,
          lineStyle: { width: 2, color: colors.outbound },
          areaStyle: { color: colors.sparklineFill },
          animationDuration: reducedMotion ? 0 : 500,
          animationEasing: 'linear',
          z: 2,
        },
      ],
    };
  }, [sparkline, colors, reducedMotion]);

  const labelRow = (
    <span className={styles.labelRow}>
      <Caption1Strong style={{ color: colors.outbound }}>{label}</Caption1Strong>
      {hint && (
        <Tooltip content={hint} relationship="description" withArrow>
          <span className={styles.hintIcon}>
            <Info16Regular />
          </span>
        </Tooltip>
      )}
    </span>
  );

  if (loading) {
    return (
      <div
        data-testid="kpi-tile"
        data-loading="true"
        aria-busy="true"
        className={mergeClasses(styles.card, size === 'hero' && styles.hero, className)}
      >
        {labelRow}
        <LoadingState variant="panel" layout="kpi" />
      </div>
    );
  }

  return (
    <div data-testid="kpi-tile" className={mergeClasses(styles.card, size === 'hero' && styles.hero, className)}>
      {labelRow}
      <div className={styles.valueRow}>
        <span ref={valueRef}>
          {size === 'hero' ? (
            <LargeTitle className={mergeClasses(styles.heroValue, noWrapValue && styles.noWrapValue)} style={valueColor ? { color: valueColor } : undefined}>{displayValue}</LargeTitle>
          ) : (
            <Title1 className={mergeClasses(styles.tileValue, noWrapValue && styles.noWrapValue)} style={valueColor ? { color: valueColor } : undefined}>{displayValue}</Title1>
          )}
        </span>
        {delta && (
          <span className={mergeClasses(styles.delta, styles[DELTA_TONE_CLASS_KEY[tone]])}>
            {DELTA_ICON[delta.direction]}
            {delta.text}
          </span>
        )}
      </div>
      {caption && <Caption1 className={styles.caption}>{caption}</Caption1>}
      {subItems && subItems.length > 0 && (
        <div data-testid="kpi-tile-subitems" className={mergeClasses(styles.subItems, subItems.length >= 3 && styles.subItemsGrid)}>
          {subItems.map((item, i) => (
            <div key={i} className={styles.subItem}>
              {item.icon && (
                <span className={mergeClasses(styles.subItemIcon, item.status && styles[SUB_ITEM_STATUS_CLASS_KEY[item.status]])}>
                  {item.icon}
                </span>
              )}
              <div className={styles.subItemText}>
                <span className={mergeClasses(styles.subItemValue, item.status && styles[SUB_ITEM_STATUS_CLASS_KEY[item.status]])}>
                  {item.value}
                </span>
                <Caption1 className={styles.subItemLabel}>{item.label}</Caption1>
              </div>
            </div>
          ))}
        </div>
      )}
      {size === 'hero' && sparkline?.length ? (
        <div ref={sparkRef} className={styles.sparkline}>
          {(sparkVisible || reducedMotion) && sparkOption && (
            <Chart option={sparkOption} height={80} ariaLabel={`${label} trend sparkline`} />
          )}
        </div>
      ) : null}
    </div>
  );
}
